import { useMemo } from 'react'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import useSearchBurnPoints from 'hooks/useSearchBurnPoints'

interface Params {
  filters?: App.OfferListFilters
  totalPrice?: number
  totalMemberPrice?: number
  loyaltyProductType: App.LoyaltyTourProductType | undefined
  isLuxPlusEnabled: boolean
  isLoyaltyEnabled: boolean
}

/**
 * Shared burn / view-in-points state for a tour search tile price stack.
 */
export default function useTourSearchTileBurn(params: Params) {
  const { filters, totalPrice, totalMemberPrice, loyaltyProductType, isLuxPlusEnabled, isLoyaltyEnabled } = params
  const { hasSelectedBurnToggle, pointsToBurn: contextPointsToBurn } = useSearchBurnEnabled()

  const viewPriceInPointsFromUrl = !!filters?.viewPriceInPoints
  const showLuxPlusPricing = isLuxPlusEnabled && !!totalMemberPrice

  // Burn slider wins if URL still has viewPriceInPoints during a transition.
  const viewPriceInPoints = viewPriceInPointsFromUrl && !hasSelectedBurnToggle
  const isSocietePricingActive = viewPriceInPoints || hasSelectedBurnToggle

  const nonMemberBurn = useSearchBurnPoints(totalPrice ?? 0, loyaltyProductType, undefined, {
    disabled: !isLoyaltyEnabled || !totalPrice || !isSocietePricingActive || !loyaltyProductType,
    viewPriceInPoints,
  })

  const memberBurn = useSearchBurnPoints(totalMemberPrice ?? 0, loyaltyProductType, undefined, {
    disabled: !isLoyaltyEnabled || !showLuxPlusPricing || !isSocietePricingActive || !loyaltyProductType,
    viewPriceInPoints,
  })

  const priceInPointsForEarn = showLuxPlusPricing && memberBurn.priceInPoints
    ? memberBurn.priceInPoints
    : nonMemberBurn.priceInPoints

  const pointsToApplyForEarn = useMemo(() => {
    if (!isSocietePricingActive || !priceInPointsForEarn) return undefined
    if (viewPriceInPoints) return priceInPointsForEarn
    if (hasSelectedBurnToggle && contextPointsToBurn !== undefined) {
      return Math.min(contextPointsToBurn, priceInPointsForEarn)
    }
    return undefined
  }, [
    contextPointsToBurn,
    hasSelectedBurnToggle,
    isSocietePricingActive,
    priceInPointsForEarn,
    viewPriceInPoints,
  ])

  return {
    hasSelectedBurnToggle,
    viewPriceInPoints,
    isSocietePricingActive,
    showLuxPlusPricing,
    nonMemberBurn,
    memberBurn,
    pointsToApplyForEarn,
    priceInPointsForEarn,
  }
}

export type TourSearchTileBurn = ReturnType<typeof useTourSearchTileBurn>
